import React from 'react';
import { st, classes } from './CodeSnippet.st.css';
import {
  COPY_BUTTON_SIZE,
  SIZE,
  VARIANT,
  DATA_HOOKS,
} from './CodeSnippet.constants';
import { CodeSnippetProps } from './CodeSnippet.types';
import { useId } from '../utils/useId';
import { CopyButton } from './components/CopyButton';
import { InlineVariant } from './components/InlineVariant';

const CodeSnippet = ({
  variant = VARIANT.singleline,
  size,
  code,
  copyText,
  disableCopy = false,
  copyTooltipText,
  copiedTooltipText,
  tooltipProps,
  onCopy,
  ariaLabel,
  wrapText = false,
  dataHook,
  className,
}: CodeSnippetProps) => {
  // Associates the copy button with the code block so screen readers announce
  // the snippet content as the button's description on focus.
  const codeId = useId('code-snippet-');

  if (variant === VARIANT.inline) {
    return (
      <InlineVariant
        code={code}
        size={size}
        copyText={copyText}
        disableCopy={disableCopy}
        copyTooltipText={copyTooltipText}
        copiedTooltipText={copiedTooltipText}
        tooltipProps={tooltipProps}
        onCopy={onCopy}
        ariaLabel={ariaLabel}
        dataHook={dataHook}
        className={className}
      />
    );
  }

  const valueToCopy = copyText ?? code;
  const blockSize = size ?? SIZE.small;
  const buttonSize =
    variant === VARIANT.singleline && blockSize !== SIZE.medium
      ? COPY_BUTTON_SIZE.small
      : COPY_BUTTON_SIZE.medium;

  return (
    <div
      data-hook={dataHook}
      className={st(
        classes.root,
        {
          variant,
          size: blockSize,
          wrapText: variant === VARIANT.multiline && wrapText,
          disableCopy,
        },
        className,
      )}
    >
      <pre
        id={codeId}
        data-hook={DATA_HOOKS.code}
        aria-label={ariaLabel}
        className={classes.code}
      >
        <code>{code}</code>
      </pre>

      {!disableCopy && (
        <div className={classes.copyButtonSlot}>
          <CopyButton
            variant={variant}
            size={buttonSize}
            value={valueToCopy}
            copyTooltipText={copyTooltipText}
            copiedTooltipText={copiedTooltipText}
            tooltipProps={tooltipProps}
            onCopy={onCopy}
            describedById={codeId}
            dataHook={DATA_HOOKS.copyButton}
          />
        </div>
      )}
    </div>
  );
};

CodeSnippet.displayName = 'CodeSnippet';

export { CodeSnippet };
